import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { useMe } from '@/hooks/api/usePortal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldCheck } from 'lucide-react';

interface RouteGuardProps {
  required: 'authenticated' | 'admin' | 'customer';
  children: ReactNode;
}

/**
 * Wraps portal layouts to enforce access control based on `/api/users/me`.
 * Guest → redirect to /auth/login. Wrong role → redirect to home.
 * Pending/inactive → shows activation-pending message.
 */
export function RouteGuard({ required, children }: RouteGuardProps) {
  const { data, isLoading, isError } = useMe();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (!data?.user) {
      nav(`/auth/login?next=${encodeURIComponent(loc.pathname)}`, { replace: true });
      return;
    }
    if (required === 'admin' && data.user.role !== 'admin') {
      nav('/dashboard', { replace: true });
    }
    if (required === 'customer' && data.user.role !== 'customer') {
      nav('/admin', { replace: true });
    }
  }, [isLoading, data, nav, required, loc.pathname]);

  if (isLoading || isError || !data?.user) return <LoadingSpinner fullscreen label="Loading…" />;

  if (required === 'customer' && data.user.status !== 'active') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="max-w-md text-center">
          <CardContent className="space-y-4 p-8">
            <ShieldCheck className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="font-display text-xl">Account pending activation</h2>
            <p className="text-sm text-muted-foreground">
              Your account is awaiting admin approval. You will be able to access the dashboard once
              your account is activated.
            </p>
            <Button variant="outline" onClick={() => nav('/')}>
              Back to home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
